const GHL_TRUSTED_ORIGIN_SUFFIXES = [
  'leadconnectorhq.com',
  'gohighlevel.com',
  'highlevel.com'
];

export const SUPPORTED_SOCIAL_PLATFORMS = [
  'facebook',
  'instagram',
  'linkedin',
  'google',
  'tiktok',
  'tiktok-business'
];

export function isTrustedGhlOrigin(origin) {
  if (!origin) return false;
  try {
    const hostname = new URL(origin).hostname.toLowerCase();
    return GHL_TRUSTED_ORIGIN_SUFFIXES.some(
      (suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`)
    );
  } catch {
    return false;
  }
}

export function parseSocialOAuthMessage(data) {
  if (!data || typeof data !== 'object') return null;

  const page = data.page;
  if (page !== 'social_media_posting' && page !== 'social-media-posting') {
    return null;
  }

  if (!data.accountId) return null;

  return {
    accountId: String(data.accountId),
    platform: data.platform || null,
    reconnectAccounts: Array.isArray(data.reconnectAccounts) ? data.reconnectAccounts : []
  };
}

function getApiBaseUrl() {
  const configured = (process.env.VUE_APP_ANALYTICS_API || '').trim();
  if (configured) {
    return configured.replace(/\/$/, '');
  }
  return window.location.origin;
}

export function resolveConnectUrl(connectUrl) {
  if (!connectUrl) return connectUrl;
  if (/^https?:\/\//i.test(connectUrl)) return connectUrl;

  const path = connectUrl.startsWith('/') ? connectUrl : `/${connectUrl}`;
  return `${getApiBaseUrl()}${path}`;
}

export function extractConnectToken(connectUrl) {
  if (!connectUrl) return '';
  try {
    const url = connectUrl.startsWith('http')
      ? new URL(connectUrl)
      : new URL(connectUrl, window.location.origin);
    return url.searchParams.get('token') || '';
  } catch {
    return '';
  }
}

export function openConnectPopup(redirectUrl) {
  if (!redirectUrl) return null;

  const width = 600;
  const height = 700;
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'scrollbars=yes'
  ].join(',');

  // Do not use noopener — GHL OAuth posts accountId back via window.opener.postMessage.
  return window.open(redirectUrl, 'ghlSocialConnect', features);
}

export function waitForSocialOAuthMessage(timeoutMs = 300000) {
  return new Promise((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
    };

    const onMessage = (event) => {
      if (!isTrustedGhlOrigin(event.origin)) return;

      const parsed = parseSocialOAuthMessage(event.data);
      if (!parsed) return;

      settled = true;
      cleanup();
      resolve(parsed);
    };

    const timer = setTimeout(() => {
      if (settled) return;
      cleanup();
      reject(new Error('Social account authorization timed out.'));
    }, timeoutMs);

    window.addEventListener('message', onMessage);
  });
}

export function formatPlatformLabel(platform) {
  if (platform === 'tiktok-business') return 'TikTok Business';
  if (platform === 'google') return 'Google Business';
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}
